import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Delete, Edit } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, setDoc, updateDoc, writeBatch } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import {
  DEANERY_OPTIONS,
  buildDeaneryDesignation,
  DeaneryMember,
  DESIGNATION_TYPE_OPTIONS,
  getDeaneryDesignationPriority,
  ORGANIZATION_OPTIONS,
  defaultDeaneryMembers,
  normalizeDeaneryMember,
  sortDeaneryMembers,
} from "../../deanery_youth_commission/deanery_data";
import { toTitleCaseName } from "../../../utils/title_case";

const EMPTY_FORM = {
  deanery: "",
  organization: "Not Applicable",
  designationType: "Director",
  designationDetail: "",
  rank: "",
  name: "",
  place: "",
  img: "",
};

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

async function resequenceDeaneryMembers(deanery: string, targetMemberId?: string) {
  const snapshot = await getDocs(collection(firestore, "deanery_members"));
  const orderedDocs = snapshot.docs
    .filter((memberDoc) => memberDoc.data().deanery === deanery)
    .sort((docA, docB) => {
      const priorityA = getDeaneryDesignationPriority(docA.data());
      const priorityB = getDeaneryDesignationPriority(docB.data());

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const rankA = Number(docA.data().rank || 0);
      const rankB = Number(docB.data().rank || 0);

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      if (targetMemberId && docA.id === targetMemberId) {
        return -1;
      }

      if (targetMemberId && docB.id === targetMemberId) {
        return 1;
      }

      return String(docA.data().name || "").localeCompare(String(docB.data().name || ""));
    });

  const batch = writeBatch(firestore);
  const timestamp = new Date().toISOString();

  orderedDocs.forEach((memberDoc, index) => {
    batch.update(memberDoc.ref, {
      rank: index + 1,
      updatedAt: timestamp,
    });
  });

  await batch.commit();
}

export default function ManageDeaneryMembersTab() {
  const { showSnackbar } = useFeedback();
  const [members, setMembers] = useState<DeaneryMember[]>([]);
  const [editingMember, setEditingMember] = useState<DeaneryMember | null>(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  async function seedDefaultMembers() {
    const timestamp = new Date().toISOString();

    await Promise.all(
      defaultDeaneryMembers.map((member) =>
        setDoc(doc(firestore, "deanery_members", `${member.deanery}-${member.designation}-${member.name}`.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()), {
          deanery: member.deanery,
          organization: member.organization,
          rank: member.rank,
          name: member.name,
          designation: member.designation,
          place: member.place || "",
          img: member.img || "",
          createdAt: timestamp,
          updatedAt: timestamp,
        }),
      ),
    );
  }

  async function fetchMembers() {
    try {
      setIsLoading(true);
      const snapshot = await getDocs(collection(firestore, "deanery_members"));

      if (!snapshot.docs.length) {
        await seedDefaultMembers();
        const seededSnapshot = await getDocs(collection(firestore, "deanery_members"));
        setMembers(
          sortDeaneryMembers(seededSnapshot.docs.map((memberDoc) => normalizeDeaneryMember({
            docId: memberDoc.id,
            ...(memberDoc.data() as Omit<DeaneryMember, "docId">),
          }))),
        );
        return;
      }

      setMembers(
        sortDeaneryMembers(snapshot.docs.map((memberDoc) => normalizeDeaneryMember({
          docId: memberDoc.id,
          ...(memberDoc.data() as Omit<DeaneryMember, "docId">),
        }))),
      );
    } catch (error) {
      console.error("Failed to load deanery members", error);
      showSnackbar("Failed to load deanery members");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleEdit(member: DeaneryMember) {
    setEditingMember(member);
    setFormValues({
      deanery: member.deanery,
      organization: member.organization,
      designationType: member.designationType || "Director",
      designationDetail: member.designationDetail || "",
      rank: String(member.rank),
      name: member.name,
      place: member.place || "",
      img: member.img || "",
    });
    setSelectedImage(null);
  }

  async function handleUpdate() {
    if (!editingMember?.docId) {
      return;
    }

    const rank = Number(formValues.rank);

    if (
      !formValues.deanery ||
      !formValues.name.trim() ||
      !Number.isInteger(rank) ||
      rank <= 0
    ) {
      showSnackbar("Please fill deanery, rank, name, and designation");
      return;
    }

    if (formValues.designationType === "Others" && !formValues.designationDetail.trim()) {
      showSnackbar("Please enter the custom designation");
      return;
    }

    try {
      setIsSaving(true);
      let finalImage = formValues.img.trim();

      if (selectedImage) {
        const storageRef = ref(storage, `deanery_members/${Date.now()}-${selectedImage.name}`);
        await uploadBytes(storageRef, selectedImage);
        finalImage = await getDownloadURL(storageRef);
      }

      const timestamp = new Date().toISOString();
      const previousDeanery = editingMember.deanery;
      const nextDeanery = formValues.deanery;

      await updateDoc(doc(firestore, "deanery_members", editingMember.docId), {
        deanery: formValues.deanery,
        organization: formValues.organization,
        designationType: formValues.designationType,
        designationDetail: formValues.designationDetail.trim(),
        rank,
        name: toTitleCaseName(formValues.name),
        designation: buildDeaneryDesignation(
          formValues.organization as any,
          formValues.designationType as any,
          formValues.designationDetail,
        ),
        place: formValues.place.trim(),
        img: normalizeUrl(finalImage),
        updatedAt: timestamp,
      });

      if (previousDeanery !== nextDeanery) {
        await resequenceDeaneryMembers(previousDeanery);
      }
      await resequenceDeaneryMembers(nextDeanery, editingMember.docId);

      showSnackbar("Deanery member updated successfully");
      setEditingMember(null);
      setFormValues(EMPTY_FORM);
      setSelectedImage(null);
      fetchMembers();
    } catch (error) {
      console.error("Failed to update deanery member", error);
      showSnackbar("Failed to update deanery member");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(member: DeaneryMember) {
    if (!member.docId) {
      return;
    }

    if (!window.confirm(`Delete "${member.name}"?`)) {
      return;
    }

    try {
      const deanery = member.deanery;
      await deleteDoc(doc(firestore, "deanery_members", member.docId));
      await resequenceDeaneryMembers(deanery);
      showSnackbar("Deanery member deleted successfully");
      fetchMembers();
    } catch (error) {
      console.error("Failed to delete deanery member", error);
      showSnackbar("Failed to delete deanery member");
    }
  }

  if (isLoading) {
    return (
      <Box py={6} textAlign="center">
        <Typography>Loading deanery members...</Typography>
      </Box>
    );
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        {members.map((member) => (
          <Grid item xs={12} sm={6} md={4} key={member.docId || `${member.deanery}-${member.name}`}>
            <Card sx={{ height: "100%" }}>
              {member.img ? (
                <Box
                  component="img"
                  src={member.img}
                  alt={member.name}
                  sx={{
                    width: "100%",
                    height: 240,
                    objectFit: "cover",
                    objectPosition: "center top",
                  }}
                />
              ) : null}
              <CardContent>
                <Typography variant="h6" fontWeight={700}>
                  {member.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {member.deanery}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {member.designation}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rank: {member.rank}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {member.organization}
                </Typography>
                {member.place ? (
                  <Typography variant="body2" color="text.secondary">
                    {member.place}
                  </Typography>
                ) : null}
                <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                  <Button variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(member)}>
                    Edit
                  </Button>
                  <Button color="error" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(member)}>
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(editingMember)} onClose={() => setEditingMember(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Deanery Member</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <TextField
              select
              fullWidth
              label="Deanery"
              value={formValues.deanery}
              onChange={(event) => setFormValues((prev) => ({ ...prev, deanery: event.target.value }))}
            >
              {DEANERY_OPTIONS.map((deanery) => (
                <MenuItem key={deanery} value={deanery}>
                  {deanery}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Designation"
              value={formValues.designationType}
              onChange={(event) =>
                setFormValues((prev) => ({
                  ...prev,
                  designationType: event.target.value,
                  designationDetail: event.target.value === "Others" ? prev.designationDetail : "",
                }))
              }
            >
              {DESIGNATION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Organization"
              value={formValues.organization}
              onChange={(event) => setFormValues((prev) => ({ ...prev, organization: event.target.value }))}
            >
              {ORGANIZATION_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              required
              type="number"
              label="Rank"
              value={formValues.rank}
              onChange={(event) => setFormValues((prev) => ({ ...prev, rank: event.target.value }))}
              inputProps={{ min: 1, step: 1 }}
              helperText="Lower rank appears first inside the same role group. Directors always stay above presidents."
            />
            <TextField
              fullWidth
              label="Member Name"
              value={formValues.name}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, name: toTitleCaseName(event.target.value) }))
              }
            />
            <TextField
              fullWidth
              label={formValues.designationType === "Others" ? "Custom Designation" : "Generated Designation"}
              value={
                formValues.designationType === "Others"
                  ? formValues.designationDetail
                  : buildDeaneryDesignation(
                      formValues.organization as any,
                      formValues.designationType as any,
                      formValues.designationDetail,
                    )
              }
              onChange={(event) => {
                if (formValues.designationType === "Others") {
                  setFormValues((prev) => ({ ...prev, designationDetail: event.target.value }));
                }
              }}
              InputProps={formValues.designationType === "Others" ? undefined : { readOnly: true }}
            />
            <TextField
              fullWidth
              label="Place"
              value={formValues.place}
              onChange={(event) => setFormValues((prev) => ({ ...prev, place: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Image URL"
              value={formValues.img}
              onChange={(event) => {
                setFormValues((prev) => ({ ...prev, img: event.target.value }));
                if (event.target.value) {
                  setSelectedImage(null);
                }
              }}
              helperText="Use either an image URL or upload an image below."
            />
            <FormControl fullWidth>
              <FormLabel>Upload New Member Image</FormLabel>
              <input
                type="file"
                accept="image/*"
                style={{
                  border: "1px solid rgb(181, 176, 176)",
                  width: "96%",
                  paddingLeft: 8,
                  paddingRight: 8,
                  paddingTop: 16,
                  paddingBottom: 16,
                  borderRadius: 5,
                  marginTop: 8,
                }}
                onChange={(event) => {
                  if (event.target.files?.[0]) {
                    setSelectedImage(event.target.files[0]);
                    setFormValues((prev) => ({ ...prev, img: "" }));
                  }
                }}
              />
              {selectedImage ? (
                <Box mt={2} position="relative" display="inline-block">
                  <img
                    src={URL.createObjectURL(selectedImage)}
                    alt="Updated deanery member preview"
                    style={{
                      maxWidth: "220px",
                      maxHeight: "220px",
                      borderRadius: 8,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setSelectedImage(null)}
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      color: "#ffffff",
                      backgroundColor: "rgba(0,0,0,0.5)",
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
              ) : formValues.img ? (
                <Box mt={2}>
                  <Box
                    component="img"
                    src={formValues.img}
                    alt="Current deanery member"
                    sx={{
                      maxWidth: 220,
                      maxHeight: 220,
                      borderRadius: 2,
                    }}
                  />
                </Box>
              ) : null}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditingMember(null);
              setSelectedImage(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={handleUpdate} variant="contained" disabled={isSaving}>
            {isSaving ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
